import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  ShoppingBag, BookOpen, Gamepad2,
  Shirt, ArrowRight, LogIn, Search,
  Shield, Users, Zap,
} from 'lucide-react';
import './HomePage.css';

const CATEGORY_LABELS = {
  notes: 'Notes', gaming: 'Gaming', clothing: 'Clothing',
  electronics: 'Electronics', books: 'Books',
  sports: 'Sports', food: 'Food', other: 'Other',
};

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api.get('/products?page=1')
      .then(({ data }) => setProducts(data.products.slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleProtectedAction = () => {
    if (!user) navigate('/login');
    else navigate('/app/dashboard');
  };

  return (
    <div className="home-page">

      {/* ── Hero ── */}
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-hero-badge">
            <Zap size={13} /> Exclusive for GLA University Students
          </div>

          <h1 className="home-hero-title">
            Your Campus.<br />
            <span style={{ color: 'var(--accent)' }}>Your Marketplace.</span>
          </h1>

          <p className="home-hero-sub">
            Buy and sell notes, gear, clothing and more — directly
            with fellow GLA students. No middlemen, no fees.
          </p>

          <div className="home-hero-actions">
            <Link to="/explore" className="btn btn-primary btn-lg">
              <Search size={18} /> Browse Marketplace
            </Link>
            {user
              ? <Link to="/app/sell" className="btn btn-secondary btn-lg">
                  <ShoppingBag size={18} /> List an Item
                </Link>
              : <Link to="/login" className="btn btn-secondary btn-lg">
                  <LogIn size={18} /> Sign In to Sell
                </Link>
            }
          </div>

          {/* Stats */}
          <div className="home-stats">
            {[
              { value: '500+', label: 'Students' },
              { value: '₹0',   label: 'Platform fee' },
              { value: '1min', label: 'To list' },
            ].map(s => (
              <div key={s.label} className="home-stat">
                <span className="home-stat-val">{s.value}</span>
                <span className="home-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero image / illustration */}
        <div className="home-hero-visual">
          <div className="home-hero-card-stack">
            {['📒', '🎮', '👕', '📱'].map((emoji, i) => (
              <div key={i} className="home-hero-float-card" style={{ '--i': i }}>
                <span>{emoji}</span>
              </div>
            ))}
            <div className="home-hero-center-icon">
              <ShoppingBag size={40} color="white" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Categories ── */}
      <section className="home-section">
        <h2 className="home-section-title">Browse by Category</h2>
        <div className="home-categories">
          {[
            { icon: BookOpen,  label: 'Notes',       cat: 'notes',       color: '#3b82f6', bg: '#eff6ff' },
            { icon: Gamepad2,  label: 'Gaming',      cat: 'gaming',      color: '#8b5cf6', bg: '#f5f3ff' },
            { icon: Shirt,     label: 'Clothing',    cat: 'clothing',    color: '#ec4899', bg: '#fdf2f8' },
            { icon: ShoppingBag, label: 'Electronics', cat: 'electronics', color: '#10b981', bg: '#f0fdf4' },
          ].map(c => (
            <Link
              key={c.cat}
              to={'/explore?category=' + c.cat}
              className="home-category-card"
              style={{ '--cat-color': c.color, '--cat-bg': c.bg }}
            >
              <div className="home-category-icon">
                <c.icon size={24} />
              </div>
              <span>{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Live marketplace products ── */}
      <section className="home-section">
        <div className="home-section-header">
          <h2 className="home-section-title">Latest Listings</h2>
          <Link to="/explore" className="home-see-all">
            See all <ArrowRight size={15} />
          </Link>
        </div>

        {loading ? (
          <div className="home-products-grid">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="home-product-skeleton" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            No listings yet. Be the first to sell!
          </div>
        ) : (
          <div className="home-products-grid">
            {products.map(product => (
              <PublicProductCard
                key={product._id}
                product={product}
                isLoggedIn={!!user}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Features ── */}
      <section className="home-section">
        <h2 className="home-section-title">Why GLA Exchange?</h2>
        <div className="home-features">
          {[
            {
              icon: Shield,
              title: 'Students Only',
              desc:  'Only verified GLA University students can join. Safe and trusted community.',
            },
            {
              icon: Users,
              title: 'Direct Contact',
              desc:  'Connect directly via WhatsApp or call. No commissions, no middlemen.',
            },
            {
              icon: Zap,
              title: 'List in 1 Minute',
              desc:  'Upload photos, set price and publish. Your listing goes live instantly.',
            },
          ].map(f => (
            <div key={f.title} className="home-feature-card card">
              <div className="home-feature-icon">
                <f.icon size={22} />
              </div>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA banner ── */}
      {!user && (
        <section className="home-cta-banner">
          <h2>Ready to buy or sell?</h2>
          <p>Sign in with your GLA University Google account to get started.</p>
          <Link to="/login" className="btn btn-primary btn-lg">
            <LogIn size={18} /> Sign In with Google
          </Link>
        </section>
      )}

    </div>
  );
}

// ── Public product card ────────────────────────────────────
function PublicProductCard({ product, isLoggedIn }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/item/' + product._id);
  };

  return (
    <div className="home-product-card card card-hover" onClick={handleClick}>
      <div className="home-product-img">
        {product.images?.[0]
          ? <img src={product.images[0]} alt={product.title} loading="lazy" />
          : <div className="home-product-no-img">📦</div>
        }
        <span className={'home-product-badge badge badge-' + product.category}>
          {CATEGORY_LABELS[product.category] || product.category}
        </span>
      </div>
      <div className="home-product-body">
        <h4 className="home-product-title">{product.title}</h4>
        <div className="home-product-footer">
          <span className="home-product-price">₹{product.price.toLocaleString()}</span>
          <span className="home-product-seller">
            {product.sellerSnapshot?.name?.split(' ')[0]}
          </span>
        </div>
      </div>
    </div>
  );
}